import { useEffect, useRef, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { beginRequest, finishRequest, pendingRequest, rejectedRequest } from "@/lib/idempotency";
import { fmtDateTime } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Wrench, PackageSearch, CheckCircle2 } from "lucide-react";

const STATUS_CLS = {
  PENDENTE: "bg-rose-950/60 text-rose-300",
  PARCIAL: "bg-amber-950/60 text-amber-300",
  CONCLUIDA: "bg-emerald-950/60 text-emerald-300",
};

function ConfirmDialog({ pend, open, onOpenChange, onDone }) {
  const { user } = useAuth();
  const endpoint = `/maintenance/pendencies/${pend?.id}/confirm`;
  const userId = user?.id;
  const tenantId = user?.tenant_id;
  const inFlight = useRef(false);
  const [util, setUtil] = useState(0);
  const [dev, setDev] = useState(0);
  const [obs, setObs] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) {
      const saved = pendingRequest({ id: userId, tenant_id: tenantId }, endpoint)?.payload;
      setUtil(saved?.quantidade_utilizada ?? 0); setDev(saved?.quantidade_devolvida ?? 0); setObs(saved?.observacao || "");
      if (saved) toast.info("Confirmação anterior sem resposta. Tente novamente com os mesmos dados para recuperar o resultado.");
    }
  }, [open, endpoint, userId, tenantId]);
  if (!pend) return null;
  const pendente = pend.quantidade_pendente;
  const soma = Number(util || 0) + Number(dev || 0);
  const pending = pendingRequest(user, endpoint);
  const isReplay = pending && JSON.stringify(pending.payload) === JSON.stringify({
    quantidade_utilizada: Number(util || 0), quantidade_devolvida: Number(dev || 0), observacao: obs,
  });
  const invalid = !Number.isSafeInteger(Number(util)) || !Number.isSafeInteger(Number(dev)) ||
    Number(util) < 0 || Number(dev) < 0 || soma <= 0 || (soma > pendente && !isReplay);

  const submit = async () => {
    if (inFlight.current || invalid) return;
    inFlight.current = true; setBusy(true);
    try {
      const request = beginRequest(user, endpoint, {
        quantidade_utilizada: Number(util || 0), quantidade_devolvida: Number(dev || 0), observacao: obs,
      });
      await api.post(endpoint, request.payload, { headers: { "Idempotency-Key": request.key } });
      finishRequest(user, endpoint);
      toast.success("Destino confirmado.");
      onOpenChange(false); onDone && onDone();
    } catch (e) {
      if (rejectedRequest(e)) finishRequest(user, endpoint);
      toast.error(apiErr(e));
    } finally { inFlight.current = false; setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 border-slate-700 text-slate-100" data-testid="confirm-destino-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2"><CheckCircle2 className="h-6 w-6 text-emerald-400" /> Confirmar destino</DialogTitle>
          <DialogDescription className="text-slate-400">{pend.part_desc} · {pend.machine_nome || "—"}</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div className="rounded bg-slate-950/60 px-3 py-2"><span className="text-slate-500">Retirado por</span><div className="text-slate-100">{pend.retirado_por_nome || "—"}</div></div>
          <div className="rounded bg-slate-950/60 px-3 py-2"><span className="text-slate-500">Registrado por</span><div className="text-slate-100">{pend.registrado_por_nome || "—"}</div></div>
          <div className="rounded bg-slate-950/60 px-3 py-2"><span className="text-slate-500">Retirado</span><div className="font-mono text-slate-100">{pend.quantidade_retirada}</div></div>
          <div className="rounded bg-slate-950/60 px-3 py-2"><span className="text-slate-500">Pendente</span><div className="font-mono text-amber-300 font-bold">{pendente}</div></div>
        </div>
        <div className="space-y-3">
          <div><Label className="text-slate-300">Quantidade utilizada</Label><Input data-testid="input-util" type="number" min={0} value={util} onChange={(e) => setUtil(e.target.value)} className="mt-1 bg-slate-950 border-slate-700 font-mono h-11" /></div>
          <div><Label className="text-slate-300">Quantidade devolvida (retorna ao estoque)</Label><Input data-testid="input-dev" type="number" min={0} value={dev} onChange={(e) => setDev(e.target.value)} className="mt-1 bg-slate-950 border-slate-700 font-mono h-11" /></div>
          <div><Label className="text-slate-300">Observação</Label><Textarea value={obs} onChange={(e) => setObs(e.target.value)} className="mt-1 bg-slate-950 border-slate-700" rows={2} /></div>
          {soma > pendente && <p className="text-xs text-rose-400">Utilizada + devolvida não pode exceder o pendente ({pendente}).</p>}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button data-testid="btn-confirm-destino" onClick={submit} disabled={busy || invalid} className="bg-emerald-700 hover:bg-emerald-600">{busy ? "Salvando..." : "Confirmar"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function MaintenanceParts() {
  const { can } = useAuth();
  const canConfirm = can("ALMOXARIFADO");
  const [tab, setTab] = useState("abertas");
  const [rows, setRows] = useState(null);
  const [sel, setSel] = useState(null);

  const load = () => {
    setRows(null);
    api.get("/maintenance/pendencies", { params: { concluidas: tab === "concluidas" } }).then((r) => setRows(r.data));
  };
  useEffect(load, [tab]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-bold text-slate-50 flex items-center gap-2"><Wrench className="h-7 w-7 text-sky-400" /> Pendências Manutenção</h1>
        <p className="text-slate-400 text-sm">Peças que saíram do almoxarifado e aguardam confirmação do destino final (uso ou devolução). Cada retirada é controlada individualmente.</p>
      </div>

      <div className="flex gap-2">
        {[["abertas", "Abertas"], ["concluidas", "Concluídas"]].map(([k, label]) => (
          <Button key={k} data-testid={`maint-tab-${k}`} size="sm" variant={tab === k ? "default" : "secondary"} onClick={() => setTab(k)} className={tab === k ? "bg-sky-700" : ""}>{label}</Button>
        ))}
      </div>

      {rows === null ? <Skeleton className="h-96 bg-slate-800" /> : (
        <Card className="card-metal p-0 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[900px]">
              <thead><tr className="bg-slate-950 text-slate-400 text-xs uppercase font-mono">
                <th className="text-left px-3 py-3">Peça</th><th className="text-left px-3 py-3">Máquina</th>
                <th className="text-left px-3 py-3">Retirado por</th><th className="text-left px-3 py-3">Registrado por</th>
                <th className="text-right px-3 py-3">Retirado</th><th className="text-right px-3 py-3">Utilizado</th>
                <th className="text-right px-3 py-3">Devolvido</th><th className="text-right px-3 py-3">Pendente</th>
                <th className="text-left px-3 py-3">Data</th><th className="text-center px-3 py-3">Status</th><th className="text-right px-3 py-3">Ação</th>
              </tr></thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} data-testid={`pendency-row-${r.id}`} className="border-t border-slate-800 hover:bg-slate-900/40">
                    <td className="px-3 py-2.5 text-slate-100">{r.part_desc}</td>
                    <td className="px-3 py-2.5 text-slate-300">{r.machine_nome || "—"}</td>
                    <td className="px-3 py-2.5 text-slate-200">{r.retirado_por_nome || "—"}</td>
                    <td className="px-3 py-2.5 text-slate-400">{r.registrado_por_nome || "—"}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-rose-300">{r.quantidade_retirada}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-slate-300">{r.quantidade_utilizada}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-sky-300">{r.quantidade_devolvida}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-amber-300 font-bold">{r.quantidade_pendente}</td>
                    <td className="px-3 py-2.5 text-slate-500 whitespace-nowrap">{fmtDateTime(r.data)}</td>
                    <td className="px-3 py-2.5 text-center"><span className={`rounded px-2 py-0.5 text-xs font-medium ${STATUS_CLS[r.status] || "bg-slate-800"}`}>{r.status}</span></td>
                    <td className="px-3 py-2.5 text-right">
                      {r.quantidade_pendente > 0 && canConfirm && (
                        <Button size="sm" className="bg-emerald-700 hover:bg-emerald-600 h-8" data-testid={`btn-confirmar-${r.id}`} onClick={() => setSel(r)}><CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Confirmar destino</Button>
                      )}
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && <tr><td colSpan={11} className="px-4 py-10 text-center text-slate-500"><PackageSearch className="h-8 w-8 mx-auto mb-2 opacity-50" />{tab === "abertas" ? "Nenhuma pendência aberta." : "Nenhuma pendência concluída."}</td></tr>}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <ConfirmDialog pend={sel} open={!!sel} onOpenChange={(v) => !v && setSel(null)} onDone={load} />
    </div>
  );
}
